import React, { useContext, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import _ from 'lodash';
import { type ExperienceSimplePleaWithReturnCard } from '@bbkAdminRedux/rtkq/experience.slice';
import { type RestMultiRowSurveyCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { ExitSurveyIcons } from '@bbkAdminUtils/icons/funnel-icons';
import {
  type EventSyncObject,
  type ExecuteEventsHandler,
} from '@bbkFunnel/events';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import CustomerHtml from '@bbkFunnelComponents/customer_html/customer_html';
import DesignElement from '@bbkFunnelComponents/design-element/design-element';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { WrapIcon } from '@bbkFunnelComponents/modals/sections/action-button-helpers';
import { useMediaQueries } from '@bbkFunnelComponents/modals/sections/media-queries';
import { FunnelButton } from '@bbkFunnelComponents/v2';
import {
  REFS_CONTEXT_FUNNEL_PREFIX,
  RefsContext,
} from '@bbkFunnelUtils/refs.context';
import { makeClassNameSafe } from '@bbkFunnelUtils/utils';

import './warn.scss';

type Props = {
  card: ExperienceSimplePleaWithReturnCard;
  executeEvents: ExecuteEventsHandler;
  syncData: {
    session: string;
    version: string;
    app_id: string | null;
    survey_state: RestMultiRowSurveyCard['provides'];
  };
};

export const Warn: React.FC<Props> = (props) => {
  // console.log('[WARN]', props);
  const { card, syncData, executeEvents } = props;
  const { requires, style_overlay: styleOverlay } = card;
  const { session, version, app_id, survey_state } = syncData;
  const { type, action, url, validation, sync, text } =
    card?.requires?.backwards || {};
  const theme = useTheme() as EmotionTheme;
  const { mobileView } = useMediaQueries();
  const equalButtonSize = theme.complianceEqualButtonSize;
  const { isDesigner, isPreview } = useContext(FunnelContext);
  const refsPrefix = REFS_CONTEXT_FUNNEL_PREFIX;
  const refsCtx = useContext(RefsContext);
  const sectionRef = useRef<HTMLDivElement>(null);
  useEffect(
    () => refsCtx?.addRef(`${refsPrefix}-warn`, sectionRef),
    [sectionRef]
  );
  const iconKey = _.get(
    styleOverlay,
    'button.icon.name',
    'bbk-esi-arrow-left-1'
  );

  const warnButtonIcon = ExitSurveyIcons[iconKey]?.svg && (
    <span className="warn-button-icon d-flex align-items-center">
      <WrapIcon
        styles={{
          paddingLeft: '0px',
          paddingRight: '0px',
          width: '26px',
          height: '26px',
        }}
      >
        {ExitSurveyIcons[iconKey]?.svg}
      </WrapIcon>
    </span>
  );

  const styles = {
    fontSize: `calc(${theme.buttonFontSize || '1rem'} * 1.3)`,
    letterSpacing: '-0.36px',
    display: 'flex',
    alignItems: 'center',
    margin: '0 auto',
  };

  const backgroundColor = _.get(
    styleOverlay,
    'button.button_style.backgroundColor'
  );
  const background_color = _.get(
    styleOverlay,
    'button.button_style.background_color'
  );

  const specificStyles = {
    ...styles,
    backgroundColor: backgroundColor || background_color,
  };

  const warningDisabled = requires.plea.disabled && requires.backwards.disabled;

  if ((card.disabled || warningDisabled) && !isDesigner && !isPreview)
    return null;

  return (
    <div>
      <DesignElement name="Warning">
        <div
          className={cs('warn', makeClassNameSafe(card.name))}
          ref={sectionRef}
        >
          <div
            css={{
              opacity: `${isPreview && requires.plea.disabled ? 0.5 : 1}`,
            }}
            hidden={!isPreview && requires.plea.disabled}
          >
            {requires.plea.title && (
              <div
                className="important"
                style={{
                  ...styleOverlay.title,
                }}
              >
                <CustomerHtml value={requires.plea.title} />
              </div>
            )}
            <div className="headline">
              <CustomerHtml value={requires.plea.headline} />
            </div>
          </div>
          {/* For mobile LA have extra 15px padding when buttons have 100% width, to make it same added extra 15px here */}
          <div
            css={{
              opacity: `${isPreview && requires.backwards.disabled ? 0.5 : 1}`,
              ...(equalButtonSize &&
                mobileView && {
                  marginLeft: '15px',
                  marginRight: '15px',
                }),
            }}
            hidden={!isPreview && requires.backwards.disabled}
          >
            <FunnelButton
              onClick={() => {
                const eventObj = {
                  session,
                  version,
                  app_id,
                  id: card.id,
                  name: card.name,
                  event: 'nevermind',
                  properties: {
                    survey_state,
                  },
                };
                const syncObj: EventSyncObject = {
                  session,
                  version,
                  app_id,
                  id: card.id,
                  name: card.name,

                  direction: 'backwards',
                  type,
                  action,
                  url,
                  validation,
                  form: sync,
                  survey_state,
                };
                executeEvents({
                  synchronous: true,
                  events: [eventObj],
                  syncs: [syncObj],
                });
              }}
              specificStyles={specificStyles}
            >
              {warnButtonIcon}
              <span>{text}</span>
            </FunnelButton>
          </div>
        </div>
      </DesignElement>
    </div>
  );
};
